"use client";
import { Fragment, useState } from "react";
import BFLLink from "@repo/shared-ui/BFLLink";
import Products from "./Products";
import {
  handleFooterAccordionClickDL,
  handleFooterLinkClickDL,
} from "./FooterAccordionFunction";
import Conditional from "@repo/shared-ui/Conditional";

interface AccordionItem {
  title: string;
  content: Record<string, string>[];
}

interface FooterAccordionProps {
  loanLinks: Record<string, string>[];
  reachUsData: Record<string, string>[];
  calculatorData: Record<string, string>[];
  legalData: Record<string, string>[];
  productsData: Record<string, string>[];
  socialMediaIcons: Record<string, string>[];
}
const RenderSocialMediaIcons = (type: string, url: string, index: number) => {
  const commonClass = "w-8 h-8 rounded-full p-1 flex-center-center text-sm";
  const ytClass = "bg-yt-red";
  const fbClass = "bg-fb-blue";
  const xClass = "bg-black";
  const lInClass = "bg-lin-blue";

  return (
    <Fragment key={`${type}-${index}`}>
      <Conditional if={type === "Facebook"}>
        <BFLLink href={url} className={`${commonClass} ${fbClass}`}>
          <i className="icomoon before:content-['\e915']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "LinkedIn"}>
        <BFLLink href={url} className={`${commonClass} ${lInClass}`}>
          <i className="icomoon before:content-['\e917']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "Twitter"}>
        <BFLLink href={url} className={`${commonClass} ${xClass}`}>
          <i className="icomoon before:content-['\e95a']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "YouTube"}>
        <BFLLink href={url} className={`${commonClass} ${ytClass}`}>
          <i className="icomoon before:content-['\e920']" />
        </BFLLink>
      </Conditional>
    </Fragment>
  );
};

const FooterAccordion: React.FC<FooterAccordionProps> = ({
  loanLinks,
  reachUsData,
  calculatorData,
  legalData,
  productsData,
  socialMediaIcons,
}) => {
  const accordionData: AccordionItem[] = [
    {
      title: "Application forms",
      content: loanLinks,
    },
    {
      title: "Products portfolio",
      content: productsData,
    },
    {
      title: "Calculators",
      content: Object.values(calculatorData),
    },
    {
      title: "Legal",
      content: Object.values(legalData),
    },
    {
      title: "Reach Us",
      content: Object.values(reachUsData),
    },
  ];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleAccordion = (index: number) => {
    // Track analytics event for accordion click
    const title = accordionData[index]?.title ?? "";
    // Pass loginStatus (empty string), title, and cta_text
    handleFooterAccordionClickDL("", title, title);

    setOpenIndex(openIndex === index ? null : index);
  };
  return (
    <div className="mx-auto w-full cursor-pointer">
      {accordionData.map((item, index) => (
        <div
          key={"footacc-" + index}
          className="border-stealblue-100 border-b px-4 py-3"
        >
          <button
            onClick={() => toggleAccordion(index)}
            className="bg-bfl-blue flex w-full cursor-pointer items-center justify-between text-left text-base font-medium capitalize transition"
            data-section-title={item.title}
          >
            {item.title}
            <i
              className={`icomoon transform transition-transform duration-300 before:content-['\\e909'] ${
                openIndex === index ? "rotate-180" : ""
              }`}
            ></i>
          </button>
          <div
            className={`bg-bfl-blue transition-all duration-300 ease-in-out ${
              openIndex === index ? "" : "hidden"
            } ${
              item.title === "Reach Us" ? "flex flex-col md:flex-row" : "block"
            }`}
          >
            {item.title === "Products portfolio" ? (
              <Products data={productsData} />
            ) : (
              <>
                <div
                  className={`flex flex-wrap text-sm ${
                    item.title === "Reach Us" ? "w-full lg:!w-[40%]" : "w-full"
                  }`}
                >
                  {(() => {
                    if (item.title === "Application forms") {
                      const contentArr = Array.isArray(item.content)
                        ? item.content
                        : [];
                      const first = contentArr[0] as any;
                      if (first && Array.isArray(first.children)) {
                        return first.children.map((link: any, idx: number) => (
                          <BFLLink
                            key={link?.nodeName || link?.linkTitle || idx}
                            href={link.linkUrl || "#"}
                            className="my-1 w-[50%] pr-2 leading-[19.2px] md:!w-1/5"
                            onClick={() =>
                              handleFooterLinkClickDL(
                                link?.linkTitle || "",
                                item.title,
                                link.linkUrl || "#",
                              )
                            }
                          >
                            {link?.linkTitle}
                          </BFLLink>
                        ));
                      }
                      return null;
                    } else {
                      return (item?.content as Record<string, string>[])?.map(
                        (link, idx) => (
                          <BFLLink
                            key={link?.nodeName || link?.linkTitle || idx}
                            href={link.linkUrl || "#"}
                            className={`my-1 w-[50%] leading-[19.2px] ${item.title === "Reach Us" ? "pr-0 md:w-[50%]" : "pr-2 md:!w-1/5"}`}
                            onClick={() =>
                              handleFooterLinkClickDL(
                                link?.linkTitle || "",
                                item.title,
                                link.linkUrl || "#",
                              )
                            }
                          >
                            {link?.linkTitle}
                          </BFLLink>
                        ),
                      );
                    }
                  })()}
                </div>
                {item.title === "Reach Us" && (
                  <div className="flex gap-4 py-2 md:px-6">
                    {Object.values(socialMediaIcons).map(
                      ({ titleText, socialMediaUrl }, idx) =>
                        RenderSocialMediaIcons(
                          titleText || "",
                          socialMediaUrl || "",
                          idx,
                        ),
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
export default FooterAccordion;
